// src/pages/SettingsSoundHaptics.jsx
import React from "react";
import { useFeedback } from "../contexts/FeedbackContext";
import AccountSecurityPanel from "../components/Auth/AccountSecurityPanel";
import PageLayout from "../ui/PageLayout.jsx";
import PageHeader from "../ui/PageHeader.jsx";
import WidgetShell from "../ui/WidgetShell.jsx";
import Button from "../ui/Button.jsx";
import Switch from "../ui/Switch.jsx";
import { SmallText, BodyText, PageTitle } from "../ui/Typography.jsx";
import { useNotificationPrefs } from "../core/notifications/notificationPrefs.js";

export default function SettingsSoundHaptics() {
  const { play, buzz, prefs, setSoundEnabled, setHapticsEnabled, setVolume } = useFeedback();
  const { prefs: notifPrefs, setPrefs: setNotifPrefs } = useNotificationPrefs();

  const volPct = Math.round((prefs.volume || 0) * 100);

  return (
    <PageLayout
      header={<PageHeader title="Sound & Haptics" showBack />}
    >
      <div className="space-y-6">
        <WidgetShell title="Profile">
          <AccountSecurityPanel />
        </WidgetShell>

        <WidgetShell title="Studio">
          <BodyText className="text-[var(--mudo-text-muted)]">
            Studio details are managed in Studio settings. Use this area to review profile and security.
          </BodyText>
        </WidgetShell>

        <WidgetShell
          title="Theme & appearance"
          subtitle={<SmallText className="text-[var(--mudo-text-muted)]">Adjust feedback preferences</SmallText>}
        >
          <div className="space-y-4 text-[var(--mudo-text)]">
            <Row
              label="Enable sounds"
              control={<Switch checked={!!prefs.soundEnabled} onChange={setSoundEnabled} aria-label="Toggle sounds" />}
            />
            <Row
              label="Enable haptics"
              hint="Phone vibration or native haptics where supported"
              control={<Switch checked={!!prefs.hapticsEnabled} onChange={setHapticsEnabled} aria-label="Toggle haptics" />}
            />
            <div className="space-y-2">
              <SmallText className="font-medium text-[var(--mudo-text)]">Volume</SmallText>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={volPct}
                  onChange={(e) => setVolume(Number(e.target.value) / 100)}
                  className="w-64 accent-[var(--mudo-primary)]"
                  aria-label="Volume"
                />
                <SmallText className="w-10 text-right text-[var(--mudo-text-muted)]">{volPct}%</SmallText>
              </div>
            </div>
            <WidgetShell title="Test" className="border border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)]">
              <div className="flex flex-wrap gap-2">
                <Button variant="soft" onClick={() => (play("tap"), buzz("light"))}>Tap</Button>
                <Button variant="soft" onClick={() => (play("start"), buzz("medium"))}>Start</Button>
                <Button variant="soft" onClick={() => (play("stop"), buzz("success"))}>Stop</Button>
                <Button variant="soft" onClick={() => (play("success"), buzz("success"))}>Success</Button>
                <Button variant="soft" onClick={() => (play("error"), buzz("error"))}>Error</Button>
                <Button variant="soft" onClick={() => (play("wake"), buzz("light"))}>Wake</Button>
              </div>
            </WidgetShell>
            <BodyText className="text-xs sm:text-sm text-[var(--mudo-text-muted)]">
              Tip: Keep sounds short (&lt;150 ms). On web, actions triggered by a user tap are most reliable.
            </BodyText>
          </div>
        </WidgetShell>

        <WidgetShell title="Notifications & Alerts">
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <SmallText className="text-[var(--mudo-text)] font-medium">Planner alerts</SmallText>
                <SmallText className="text-[var(--mudo-text-muted)]">Enable planner due/overdue notifications</SmallText>
              </div>
              <Switch
                checked={!!notifPrefs.enablePlannerAlerts}
                onChange={(val) => setNotifPrefs({ enablePlannerAlerts: val })}
              />
            </div>

            <div className="flex items-center justify-between gap-2">
              <div>
                <SmallText className="text-[var(--mudo-text)] font-medium">Shop alerts</SmallText>
                <SmallText className="text-[var(--mudo-text-muted)]">Messages and offers from the shop</SmallText>
              </div>
              <Switch
                checked={!!notifPrefs.enableShopAlerts}
                onChange={(val) => setNotifPrefs({ enableShopAlerts: val })}
              />
            </div>

            <div className="flex items-center justify-between gap-2">
              <div>
                <SmallText className="text-[var(--mudo-text)] font-medium">Sync alerts</SmallText>
                <SmallText className="text-[var(--mudo-text-muted)]">Sync health and backup reminders</SmallText>
              </div>
              <Switch
                checked={!!notifPrefs.enableSyncAlerts}
                onChange={(val) => setNotifPrefs({ enableSyncAlerts: val })}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="flex flex-col gap-1">
                <SmallText className="text-[var(--mudo-text)] font-medium">Quiet hours start</SmallText>
                <input
                  type="time"
                  value={notifPrefs.quietHoursStart || ""}
                  onChange={(e) => setNotifPrefs({ quietHoursStart: e.target.value || null })}
                  className="rounded-md border px-2 py-1 border-[var(--mudo-border)] bg-[var(--mudo-bg)] text-[var(--mudo-text)] focus:outline-none focus:ring-2 focus:ring-[var(--mudo-primary)]"
                />
              </label>
              <label className="flex flex-col gap-1">
                <SmallText className="text-[var(--mudo-text)] font-medium">Quiet hours end</SmallText>
                <input
                  type="time"
                  value={notifPrefs.quietHoursEnd || ""}
                  onChange={(e) => setNotifPrefs({ quietHoursEnd: e.target.value || null })}
                  className="rounded-md border px-2 py-1 border-[var(--mudo-border)] bg-[var(--mudo-bg)] text-[var(--mudo-text)] focus:outline-none focus:ring-2 focus:ring-[var(--mudo-primary)]"
                />
              </label>
            </div>
          </div>
        </WidgetShell>

        <WidgetShell title="Data & Storage">
          <BodyText className="text-[var(--mudo-text-muted)]">
            Data and storage settings are managed in Studio Data Settings. Your current preferences remain unchanged.
          </BodyText>
        </WidgetShell>
      </div>
    </PageLayout>
  );
}

function Row({ label, hint, control }) {
  return (
    <div className="flex items-center justify-between gap-3 p-3 rounded-xl border border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)]">
      <div className="text-[var(--mudo-text)]">
        <div className="text-sm font-medium">{label}</div>
        {hint && <div className="text-xs opacity-60">{hint}</div>}
      </div>
      <div className="shrink-0">{control}</div>
    </div>
  );
}
